import { defineStore } from "pinia";

import { getCompanyId } from "@/utils/userUtils";

import fleetRepairsApi from "@/services/api/fleetRepairsApi";

import { useNotificationStore } from "@/stores/notificationStore";

export const useFleetRepairStore = defineStore("fleetRepair", {
  state: () => ({
    repairs: [],
    repairSelectItems: [],
    selectedRepair: null,
    companyId: getCompanyId(),
    dataIsLoading: false,
    isSubmitting: false,
    lastFetchParams: {},
    listRequestId: 0,
    countVehicleId: null,
    vehicleRepairsCount: null,
    countRequestId: 0,
    pagination: {
      page: 1,
      pages: 1,
      series: [],
      next: null,
      prev: null,
      count: 0,
      limit: 40,
      from: 0,
      to: 0,
    },
    statusOptions: [
      { value: "done", label: "Выполнено", color: "#16a34a" },
      { value: "planned", label: "Запланировано", color: "#ca8a04" },
      { value: "in_progress", label: "В работе", color: "#2563eb" },
      { value: "cancelled", label: "Отменено", color: "#6b7280" },
    ],
    itemTypeOptions: [
      { value: "labor", label: "Работа" },
      { value: "part", label: "Запчасть" },
      { value: "material", label: "Материал" },
    ],
    vatKindOptions: [
      { value: "with_vat", label: "С НДС" },
      { value: "zero", label: "НДС 0%" },
      { value: "no_vat", label: "Без НДС" },
    ],
    vatCalculationTypeOptions: [
      { value: "included", label: "НДС включён" },
      { value: "on_top", label: "НДС сверху" },
    ],
  }),

  getters: {
    statusLabels: (state) =>
      Object.fromEntries(
        state.statusOptions.map((option) => [
          option.value,
          option.label,
        ])
      ),

    itemTypeLabels: (state) =>
      Object.fromEntries(
        state.itemTypeOptions.map((option) => [
          option.value,
          option.label,
        ])
      ),

    vatKindLabels: (state) =>
      Object.fromEntries(
        state.vatKindOptions.map((option) => [
          option.value,
          option.label,
        ])
      ),

    vatCalculationTypeLabels: (state) =>
      Object.fromEntries(
        state.vatCalculationTypeOptions.map((option) => [
          option.value,
          option.label,
        ])
      ),

  },

  actions: {
    setCountVehicle(vehicleId) {
      if (String(this.countVehicleId) === String(vehicleId)) return;
      this.countRequestId += 1;
      this.countVehicleId = vehicleId;
      this.vehicleRepairsCount = null;
    },

    async fetchVehicleRepairCount() {
      const vehicleId = this.countVehicleId;
      if (!vehicleId) return;
      const requestId = ++this.countRequestId;
      const response = await fleetRepairsApi.index(this.companyId, {
        "q[vehicle_id]": vehicleId,
        "q[archived]": "active",
        page: 1,
        limit: 1,
      });
      if (requestId === this.countRequestId) {
        this.vehicleRepairsCount = response.data.pagination_meta?.count ?? null;
      }
    },

    normalizeResponseData(response) {
      return response.data.result || response.data || [];
    },

    applyPagination(response) {
      const meta = response.data.pagination_meta;
      if (!meta) return;

      this.pagination = {
        page: meta.page,
        pages: meta.pages,
        series: meta.series,
        next: meta.next,
        prev: meta.prev,
        count: meta.count,
        limit: meta.limit,
        from: meta.from,
        to: meta.to,
      };
    },

    async fetchRepairs(params = {}) {
      const requestId = ++this.listRequestId;
      this.dataIsLoading = true;
      this.lastFetchParams = params;

      try {
        const response = await fleetRepairsApi.index(
          this.companyId,
          params
        );
        // При переходе между ТС старый ответ не должен подменять новую выборку
        if (requestId !== this.listRequestId) return;
        this.repairs = this.normalizeResponseData(response);
        this.applyPagination(response);
      } finally {
        if (requestId === this.listRequestId) this.dataIsLoading = false;
      }
    },

    async fetchRepair(id) {
      this.dataIsLoading = true;

      try {
        const response = await fleetRepairsApi.show(
          this.companyId,
          id
        );
        this.selectedRepair = this.normalizeResponseData(response);
        return this.selectedRepair;
      } finally {
        this.dataIsLoading = false;
      }
    },

    async fetchRepairSelectItems(params = {}) {
      this.dataIsLoading = true;

      try {
        const response = await fleetRepairsApi.index(
          this.companyId,
          params
        );

        this.repairSelectItems = this.normalizeResponseData(response);
        return this.repairSelectItems;
      } finally {
        this.dataIsLoading = false;
      }
    },

    async fetchRepairSelectItem(id) {
      const exists =
        this.repairSelectItems.find(
          (repair) => String(repair.id) === String(id)
        ) ||
        this.repairs.find((repair) => String(repair.id) === String(id));
      if (exists) return exists;

      return await this.fetchRepair(id);
    },

    applyRepairSnapshot(repair) {
      if (!repair?.id) return;

      if (String(this.selectedRepair?.id) === String(repair.id)) {
        this.selectedRepair = repair;
      }

      this.repairs = this.repairs.map((item) =>
        String(item.id) === String(repair.id) ? repair : item
      );
      this.repairSelectItems = this.repairSelectItems.map((item) =>
        String(item.id) === String(repair.id) ? repair : item
      );
    },

    async createRepair(payload) {
      this.isSubmitting = true;

      try {
        const response = await fleetRepairsApi.create(
          this.companyId,
          payload
        );
        const repair = this.normalizeResponseData(response);

        this.selectedRepair = repair;
        useNotificationStore().showSuccess("Ремонт создан");
        await this.fetchVehicleRepairCount();
        await this.fetchRepairs(this.lastFetchParams);

        return repair;
      } finally {
        this.isSubmitting = false;
      }
    },

    async updateRepair(id, payload) {
      this.isSubmitting = true;

      try {
        const response = await fleetRepairsApi.update(
          this.companyId,
          id,
          payload
        );
        const repair = this.normalizeResponseData(response);

        this.applyRepairSnapshot(repair);
        useNotificationStore().showSuccess("Ремонт обновлён");
        await this.fetchVehicleRepairCount();

        return repair;
      } finally {
        this.isSubmitting = false;
      }
    },

    async transitionRepair(id, status, payload = {}) {
      if (!["in_progress", "done", "cancelled", "planned"].includes(status) || this.isSubmitting) return;
      this.isSubmitting = true;
      try {
        const response = await fleetRepairsApi.changeStatus(this.companyId, id, { ...payload, status });
        const repair = this.normalizeResponseData(response);
        this.applyRepairSnapshot(repair);
        useNotificationStore().showSuccess("Статус ремонта изменён");
        return repair;
      } finally {
        this.isSubmitting = false;
      }
    },

    async archiveRepair(id) {
      this.isSubmitting = true;

      try {
        const response = await fleetRepairsApi.archive(this.companyId, id);
        const repair = this.normalizeResponseData(response);

        this.applyRepairSnapshot(repair);
        useNotificationStore().showSuccess("Ремонт архивирован");
        await this.fetchVehicleRepairCount();

        return repair;
      } finally {
        this.isSubmitting = false;
      }
    },

    async restoreRepair(id) {
      this.isSubmitting = true;

      try {
        const response = await fleetRepairsApi.restore(this.companyId, id);
        const repair = this.normalizeResponseData(response);

        this.applyRepairSnapshot(repair);
        useNotificationStore().showSuccess("Ремонт восстановлен");
        await this.fetchVehicleRepairCount();

        return repair;
      } finally {
        this.isSubmitting = false;
      }
    },

    async attachExpense(id, expenseId) {
      this.isSubmitting = true;

      try {
        const response = await fleetRepairsApi.attachExpense(
          this.companyId,
          id,
          expenseId
        );
        const repair = this.normalizeResponseData(response);

        this.applyRepairSnapshot(repair);
        useNotificationStore().showSuccess("Расход привязан к ремонту");

        return repair;
      } finally {
        this.isSubmitting = false;
      }
    },

    async confirmInspection(id, payload) {
      this.isSubmitting = true;

      try {
        const response = await fleetRepairsApi.confirmInspection(
          this.companyId,
          id,
          payload
        );
        const repair = this.normalizeResponseData(response);

        this.applyRepairSnapshot(repair);
        useNotificationStore().showSuccess("Проверка ремонта подтверждена");

        return repair;
      } finally {
        this.isSubmitting = false;
      }
    },

    formatStatus(status) {
      return this.statusLabels[status] || status || "—";
    },

    statusColor(status) {
      return (
        this.statusOptions.find((option) => option.value === status)
          ?.color || "#6b7280"
      );
    },

    formatItemType(type) {
      return this.itemTypeLabels[type] || type || "—";
    },

  },
});
